/**
 * Forward paper test of the BTC 15m "price ladder" rules found on
 * 2026-10-06 (research/pattern-search, pre-registered in
 * research/pattern-search/PREREGISTERED.md before any test round existed).
 *
 * Each rule buys, once per round, whichever side's Kalshi ask is inside its
 * price band at or after its first minute, and holds to Kalshi's official
 * settlement — or, for the D rules, sells early at the side's own bid the
 * first time a later minute's bid reaches its take-profit or stop-loss. Nothing here trades: it records what each rule WOULD have
 * bought and scores it, so the rules are judged only on rounds that did not
 * exist when they were chosen.
 *
 * Pure logic: no network, no storage.
 */
import { kalshiTakerFee } from './btc15ShadowSignals';

export type PriceRuleId = 'C1' | 'C2' | 'C3' | 'D1' | 'D2' | 'E1' | 'E2';

export interface PriceRuleSpec {
  id: PriceRuleId;
  label: string;
  /** Whole minutes into the round (0-14) from which the rule may enter. */
  firstMinute: number;
  /** Ask band, low inclusive, high exclusive (dollars). */
  askLow: number;
  askHigh: number;
  /** Sell at the side's bid once it reaches this (dollars). */
  takeProfit?: number;
  /** Sell at the side's bid once it falls to this (dollars). */
  stopLoss?: number;
  /** Last whole minute the rule may enter (default 14). */
  lastMinute?: number;
  /**
   * E1: evaluated once, at the round's first reading in minutes 8–9, on the
   * favorite (the side with mid ≥ 0.5), and only if its ask is below the
   * highest ask that side showed earlier in the round.
   */
  pullback?: boolean;
  /** Only rounds closing after this instant count (its pre-registration time). */
  registeredAtMs: number;
}

/** C1–C3 were registered first; D1/D2 (exits) in round 2 the same day. */
export const PRICE_RULES_REGISTERED_AT_MS = Date.parse('2026-10-06T18:30:00Z');
const ROUND_2_REGISTERED_AT_MS = Date.parse('2026-10-06T20:14:00Z');
const ROUND_3_REGISTERED_AT_MS = Date.parse('2026-10-06T21:30:00Z');

export const PRICE_RULES: readonly PriceRuleSpec[] = [
  {
    id: 'C1',
    label: 'Favorite at 80–90¢, from minute 8',
    firstMinute: 8,
    askLow: 0.8,
    askHigh: 0.9,
    registeredAtMs: PRICE_RULES_REGISTERED_AT_MS,
  },
  {
    id: 'C2',
    label: 'Favorite at 80–90¢, from minute 12',
    firstMinute: 12,
    askLow: 0.8,
    askHigh: 0.9,
    registeredAtMs: PRICE_RULES_REGISTERED_AT_MS,
  },
  {
    id: 'C3',
    label: 'Favorite at 60–70¢, from minute 8',
    firstMinute: 8,
    askLow: 0.6,
    askHigh: 0.7,
    registeredAtMs: PRICE_RULES_REGISTERED_AT_MS,
  },
  {
    id: 'D1',
    label: 'C1 + sell at a 95¢ bid',
    firstMinute: 8,
    askLow: 0.8,
    askHigh: 0.9,
    takeProfit: 0.95,
    registeredAtMs: ROUND_2_REGISTERED_AT_MS,
  },
  {
    id: 'D2',
    label: 'C1 + sell at a 95¢ bid, stop at 60¢',
    firstMinute: 8,
    askLow: 0.8,
    askHigh: 0.9,
    takeProfit: 0.95,
    stopLoss: 0.6,
    registeredAtMs: ROUND_2_REGISTERED_AT_MS,
  },
  {
    id: 'E1',
    label: 'Pullback favorite at 55–80¢, minute 8',
    firstMinute: 8,
    lastMinute: 9,
    askLow: 0.55,
    askHigh: 0.8,
    pullback: true,
    registeredAtMs: ROUND_3_REGISTERED_AT_MS,
  },
  {
    id: 'E2',
    label: 'D1, entering only in minutes 8–12',
    firstMinute: 8,
    lastMinute: 12,
    askLow: 0.8,
    askHigh: 0.9,
    takeProfit: 0.95,
    registeredAtMs: ROUND_3_REGISTERED_AT_MS,
  },
];

/** Paper stake per bet, for the dollar column. */
export const PRICE_RULE_STAKE_DOLLARS = 10;

/** The pre-registered pass bar. */
export const PASS_BAR = {
  minBets: 300,
  minWinRate: 0.6,
  /** Net per contract after fees must exceed this... */
  minNetPerContract: 0,
  /** ...and the lower end of its ~95% range must exceed this (dollars). */
  minNetLowerBound: -0.01,
} as const;
export type PassBar = typeof PASS_BAR;

export type Side = 'yes' | 'no';

export interface PriceRuleDecision {
  rule: PriceRuleId;
  marketTicker: string;
  roundOpenMs: number;
  roundCloseMs: number;
  observedAtMs: number;
  minute: number;
  side: Side;
  ask: number;
  /** The same side's ask one minute later: a realistic-fill stress. */
  nextAsk?: number | null;
  result?: Side;
  /** D rules: the bid sold into, and when, if an exit level was hit. */
  exitBid?: number;
  exitMinute?: number;
}

const inBand = (ask: number | null, spec: PriceRuleSpec): ask is number =>
  ask !== null && ask >= spec.askLow && ask < spec.askHigh;

/**
 * Rules that enter at this minute: in their window, not yet entered this
 * round, and with one side's ask inside the band. If both sides were somehow
 * in band (impossible for bands above 50¢), YES is taken.
 */
/** What pullback rules need to know about the round so far. */
export interface RoundContext {
  /** Highest ask each side showed at earlier readings this round (null if never seen). */
  priorHigh: { yes: number | null; no: number | null };
  /** True only at the round's first reading in minute 8 or later. */
  firstLateReading: boolean;
  yesBid?: number | null;
  noBid?: number | null;
}

/** The favorite: the side whose mid is ≥ 0.5 (falls back to the higher ask). */
function favoriteSide(
  yesAsk: number | null,
  noAsk: number | null,
  yesBid: number | null | undefined,
): Side | null {
  if (yesAsk === null) return noAsk === null ? null : 'no';
  if (yesBid !== null && yesBid !== undefined)
    return (yesBid + yesAsk) / 2 >= 0.5 ? 'yes' : 'no';
  if (noAsk === null) return 'yes';
  return yesAsk >= noAsk ? 'yes' : 'no';
}

export function priceRulesFiringAt(
  minute: number,
  yesAsk: number | null,
  noAsk: number | null,
  alreadyEntered: ReadonlySet<PriceRuleId>,
  /** When given, rules registered after this round closes are skipped. */
  roundCloseMs = Infinity,
  /** Needed by pullback rules (E1); without it they never fire. */
  context?: RoundContext,
): { rule: PriceRuleId; side: Side; ask: number }[] {
  const out: { rule: PriceRuleId; side: Side; ask: number }[] = [];
  for (const spec of PRICE_RULES) {
    if (alreadyEntered.has(spec.id) || minute < spec.firstMinute) continue;
    if (minute > (spec.lastMinute ?? 14)) continue;
    if (roundCloseMs <= spec.registeredAtMs) continue;
    if (spec.pullback) {
      if (!context?.firstLateReading) continue;
      const side = favoriteSide(yesAsk, noAsk, context.yesBid);
      if (!side) continue;
      const ask = side === 'yes' ? yesAsk : noAsk;
      const high = side === 'yes' ? context.priorHigh.yes : context.priorHigh.no;
      if (inBand(ask, spec) && high !== null && ask < high)
        out.push({ rule: spec.id, side, ask });
      continue;
    }
    if (inBand(yesAsk, spec)) out.push({ rule: spec.id, side: 'yes', ask: yesAsk });
    else if (inBand(noAsk, spec)) out.push({ rule: spec.id, side: 'no', ask: noAsk });
  }
  return out;
}

const fee1 = (p: number) => 0.07 * p * (1 - p);

/** Net per one contract after the taker fee, held to settlement. */
export function netPerContract(ask: number, won: boolean): number {
  return (won ? 1 : 0) - ask - fee1(ask);
}

/** Net per one contract after fees on both legs, sold early at `exitBid`. */
export function exitNetPerContract(ask: number, exitBid: number): number {
  return exitBid - ask - fee1(ask) - fee1(exitBid);
}

/** P&L of a flat-dollar stake (whole contracts, real fee rounding). */
export function stakePnl(
  ask: number,
  won: boolean,
  stake = PRICE_RULE_STAKE_DOLLARS,
): number | null {
  if (!(ask > 0 && ask < 1)) return null;
  const contracts = Math.floor(stake / ask + 1e-9);
  if (contracts < 1) return null;
  const cost = contracts * ask + kalshiTakerFee(contracts, ask);
  return Number(((won ? contracts : 0) - cost).toFixed(4));
}

/** P&L of a flat-dollar stake bought at `ask` and sold at `exitBid` (fees on both legs). */
export function stakeExitPnl(
  ask: number,
  exitBid: number,
  stake = PRICE_RULE_STAKE_DOLLARS,
): number | null {
  if (!(ask > 0 && ask < 1)) return null;
  const contracts = Math.floor(stake / ask + 1e-9);
  if (contracts < 1) return null;
  const cost = contracts * ask + kalshiTakerFee(contracts, ask);
  const proceeds = contracts * exitBid - kalshiTakerFee(contracts, exitBid);
  return Number((proceeds - cost).toFixed(4));
}

/** A decision is final once it exited early or the round settled. */
export const isFinal = (d: PriceRuleDecision): boolean =>
  d.exitBid !== undefined || d.result !== undefined;

function betNet(d: PriceRuleDecision): number {
  return d.exitBid !== undefined
    ? exitNetPerContract(d.ask, d.exitBid)
    : netPerContract(d.ask, d.result === d.side);
}

function betStake(d: PriceRuleDecision, entry: number, stake: number): number {
  return (
    (d.exitBid !== undefined
      ? stakeExitPnl(entry, d.exitBid, stake)
      : stakePnl(entry, d.result === d.side, stake)) ?? 0
  );
}

export type Verdict = 'collecting' | 'passing' | 'failing';

export interface PriceRuleSummary {
  rule: PriceRuleId;
  label: string;
  entered: number;
  settled: number;
  /** Bets that made money (a held win, or a profitable early exit). */
  wins: number;
  /** D rules: how many bets were sold early. */
  exited: number;
  winRate: number | null;
  avgAsk: number | null;
  /** Average net per contract after fees (dollars). */
  netPerContract: number | null;
  /** ~95% range on netPerContract (dollars). */
  netLow: number | null;
  netHigh: number | null;
  /** Total P&L at a flat PRICE_RULE_STAKE_DOLLARS per bet. */
  stakePnl: number;
  /** Same, buying at the worse of this and the next minute's ask (bets with one recorded). */
  stakePnlNextFill: number;
  withNextFill: number;
  verdict: Verdict;
}

/**
 * Scores each rule on settled decisions. The verdict is "collecting" until
 * the pre-registered bet count is reached, then passing/failing against the
 * full pass bar. Never changes a rule; it only reports.
 */
export function summarizePriceRules(
  decisions: readonly PriceRuleDecision[],
  stake = PRICE_RULE_STAKE_DOLLARS,
): PriceRuleSummary[] {
  return PRICE_RULES.map((spec) => {
    const mine = decisions.filter((d) => d.rule === spec.id);
    const settled = mine.filter(isFinal);
    let wins = 0;
    let askSum = 0;
    let dollars = 0;
    let dollarsNext = 0;
    let withNext = 0;
    const nets: number[] = [];
    for (const d of settled) {
      const net = betNet(d);
      nets.push(net);
      if (net > 0) wins++;
      askSum += d.ask;
      dollars += betStake(d, d.ask, stake);
      if (d.nextAsk !== undefined && d.nextAsk !== null) {
        withNext++;
        dollarsNext += betStake(d, Math.max(d.ask, d.nextAsk), stake);
      }
    }
    const n = settled.length;
    const winRate = n ? wins / n : null;
    const net = n ? nets.reduce((a, b) => a + b, 0) / n : null;
    const se =
      n > 1 && net !== null
        ? Math.sqrt(nets.reduce((a, x) => a + (x - net) ** 2, 0) / (n - 1) / n)
        : null;
    const netLow = net !== null && se !== null ? net - 1.96 * se : null;
    const verdict: Verdict =
      n < PASS_BAR.minBets
        ? 'collecting'
        : winRate !== null &&
            winRate >= PASS_BAR.minWinRate &&
            net !== null &&
            net > PASS_BAR.minNetPerContract &&
            netLow !== null &&
            netLow > PASS_BAR.minNetLowerBound
          ? 'passing'
          : 'failing';
    return {
      rule: spec.id,
      label: spec.label,
      entered: mine.length,
      settled: n,
      wins,
      exited: settled.filter((d) => d.exitBid !== undefined).length,
      winRate,
      avgAsk: n ? askSum / n : null,
      netPerContract: net,
      netLow,
      netHigh: net !== null && se !== null ? net + 1.96 * se : null,
      stakePnl: Number(dollars.toFixed(2)),
      stakePnlNextFill: Number(dollarsNext.toFixed(2)),
      withNextFill: withNext,
      verdict,
    };
  });
}

/** Per-round running highs, for pullback rules. Only the last few rounds are kept. */
export interface RoundTrack {
  roundOpenMs: number;
  maxYesAsk: number | null;
  maxNoAsk: number | null;
  /** Whether a reading at minute 8+ has already been seen this round. */
  lateSeen: boolean;
  /** Earliest minute observed; pullback rules need the round watched from its start. */
  firstMinute: number;
}

const MAX_ROUND_TRACKS = 8;
/** E1 needs the round's high from (nearly) the start, not from whenever logging began. */
const PULLBACK_MAX_FIRST_MINUTE = 2;

export interface PriceRuleState {
  schemaVersion: 1;
  decisions: PriceRuleDecision[];
  lastRunMs: number | null;
  rounds?: RoundTrack[];
}

export function emptyPriceRuleState(): PriceRuleState {
  return { schemaVersion: 1, decisions: [], lastRunMs: null };
}

/** Enough for months of all seven rules (~250 decisions/day). */
export const MAX_PRICE_RULE_DECISIONS = 20_000;

export function parsePriceRuleState(raw: string): PriceRuleState {
  try {
    const parsed = JSON.parse(raw) as Partial<PriceRuleState>;
    if (parsed?.schemaVersion !== 1 || !Array.isArray(parsed.decisions)) {
      return emptyPriceRuleState();
    }
    return {
      schemaVersion: 1,
      decisions: parsed.decisions,
      lastRunMs: typeof parsed.lastRunMs === 'number' ? parsed.lastRunMs : null,
      rounds: Array.isArray(parsed.rounds) ? parsed.rounds : [],
    };
  } catch {
    return emptyPriceRuleState();
  }
}

export function enteredThisRound(
  state: PriceRuleState,
  roundOpenMs: number,
): Set<PriceRuleId> {
  return new Set(
    state.decisions.filter((d) => d.roundOpenMs === roundOpenMs).map((d) => d.rule),
  );
}

/**
 * Records one minute's quote: next-minute asks for last minute's entries,
 * early exits for this round's open D-rule bets (entered in an earlier
 * minute), then new entries.
 */
export function observePriceRules(
  state: PriceRuleState,
  obs: {
    marketTicker: string;
    roundOpenMs: number;
    roundCloseMs: number;
    nowMs: number;
    minute: number;
    yesAsk: number | null;
    noAsk: number | null;
    yesBid?: number | null;
    noBid?: number | null;
  },
): PriceRuleState {
  if (obs.roundCloseMs <= PRICE_RULES_REGISTERED_AT_MS) return state;
  const sideAsk = (side: Side) => (side === 'yes' ? obs.yesAsk : obs.noAsk);
  const sideBid = (side: Side) => (side === 'yes' ? obs.yesBid : obs.noBid) ?? null;
  const specOf = new Map(PRICE_RULES.map((r) => [r.id, r]));
  const decisions = state.decisions.map((d) => {
    if (d.roundOpenMs !== obs.roundOpenMs) return d;
    let next = d;
    if (d.minute === obs.minute - 1 && d.nextAsk === undefined) {
      next = { ...next, nextAsk: sideAsk(d.side) };
    }
    const spec = specOf.get(d.rule);
    const bid = sideBid(d.side);
    if (
      spec &&
      d.minute < obs.minute &&
      next.exitBid === undefined &&
      next.result === undefined &&
      bid !== null &&
      bid > 0 &&
      bid < 1 &&
      ((spec.takeProfit !== undefined && bid >= spec.takeProfit) ||
        (spec.stopLoss !== undefined && bid <= spec.stopLoss))
    ) {
      next = { ...next, exitBid: bid, exitMinute: obs.minute };
    }
    return next;
  });
  const tracks = state.rounds ?? [];
  const prior = tracks.find((t) => t.roundOpenMs === obs.roundOpenMs);
  const context: RoundContext | undefined =
    prior && prior.firstMinute <= PULLBACK_MAX_FIRST_MINUTE
      ? {
          priorHigh: { yes: prior.maxYesAsk, no: prior.maxNoAsk },
          firstLateReading: obs.minute >= 8 && !prior.lateSeen,
          yesBid: obs.yesBid,
          noBid: obs.noBid,
        }
      : undefined;
  const maxOf = (a: number | null | undefined, b: number | null) =>
    a === null || a === undefined ? b : b === null ? a : Math.max(a, b);
  const track: RoundTrack = {
    roundOpenMs: obs.roundOpenMs,
    maxYesAsk: maxOf(prior?.maxYesAsk, obs.yesAsk),
    maxNoAsk: maxOf(prior?.maxNoAsk, obs.noAsk),
    lateSeen: (prior?.lateSeen ?? false) || obs.minute >= 8,
    firstMinute: Math.min(prior?.firstMinute ?? obs.minute, obs.minute),
  };
  const rounds = [...tracks.filter((t) => t.roundOpenMs !== obs.roundOpenMs), track]
    .sort((a, b) => a.roundOpenMs - b.roundOpenMs)
    .slice(-MAX_ROUND_TRACKS);
  const fires = priceRulesFiringAt(
    obs.minute,
    obs.yesAsk,
    obs.noAsk,
    enteredThisRound(state, obs.roundOpenMs),
    obs.roundCloseMs,
    context,
  );
  const added: PriceRuleDecision[] = fires.map((f) => ({
    ...f,
    marketTicker: obs.marketTicker,
    roundOpenMs: obs.roundOpenMs,
    roundCloseMs: obs.roundCloseMs,
    observedAtMs: obs.nowMs,
    minute: obs.minute,
  }));
  return {
    ...state,
    rounds,
    decisions: [...decisions, ...added].slice(-MAX_PRICE_RULE_DECISIONS),
  };
}

/** Applies official results to closed, unsettled decisions. */
export function settlePriceRules(
  state: PriceRuleState,
  resultsByTicker: ReadonlyMap<string, 'UP' | 'DOWN'>,
  nowMs: number,
): PriceRuleState {
  return {
    ...state,
    decisions: state.decisions.map((d) => {
      if (d.result !== undefined || d.roundCloseMs >= nowMs - 60_000) return d;
      const withNext = d.nextAsk === undefined ? { ...d, nextAsk: null } : d;
      const actual = resultsByTicker.get(d.marketTicker);
      return actual ? { ...withNext, result: actual === 'UP' ? 'yes' : 'no' } : withNext;
    }),
  };
}
